import React from "react";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { createUTMQueryParams } from "@utils/signature_owner";
import { CLMButtonSecondary } from "components/CLM/v3/Buttons";
import CustomersComponent from "components/customers";
import AnimatedLogos from "components/customers/AnimatedLogos";

const NvidiaInceptionProgram = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: "BoloSign Joins the NVIDIA Inception Program",
    description:
      "A huge step forward for our team, our mission, and the future of AI-powered eSignatures.",
    author: {
      "@type": "Organization",
      name: "BoloSign",
    },
    publisher: {
      "@type": "Organization",
      name: "BoloSign",
      logo: {
        "@type": "ImageObject",
        url: "https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/Logo.png",
      },
    },
    datePublished: "2025-08-21",
    dateModified: "2025-08-21",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://www.bolosign.com/announcement/nvidia-inception-program/",
    },
  };

  return (
    <>
      <Head>
        <title>
          BoloSign Joins the NVIDIA Inception Program | Official Announcement
        </title>
        <meta
          name="description"
          content="A huge step forward for our team, our mission, and the future of AI-powered eSignatures. BoloSign has been accepted into the NVIDIA Inception Program."
        />
        <link
          rel="canonical"
          href="https://www.bolosign.com/announcement/nvidia-inception-program/"
        />

        {/* Open Graph Tags */}
        <meta
          property="og:title"
          content="BoloSign Joins the NVIDIA Inception Program"
        />
        <meta
          property="og:description"
          content="A huge step forward for our team, our mission, and the future of AI-powered eSignatures."
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:url"
          content="https://www.bolosign.com/announcement/nvidia-inception-program/"
        />
        <meta
          property="og:image"
          content="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/announcements/bolosign-nvidia-inception-program.webp"
        />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="BoloSign Joins the NVIDIA Inception Program"
        />
        <meta
          name="twitter:description"
          content="A huge step forward for our team, our mission, and the future of AI-powered eSignatures."
        />
        <meta
          name="twitter:image"
          content="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/announcements/bolosign-nvidia-inception-program.webp"
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      </Head>

      <div className="max-w-4xl mx-auto px-4 pt-12 md:pt-20">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="max-w-xl mx-auto text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            BoloSign Joins the NVIDIA Inception Program
          </h1>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"></h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A huge step forward for our team, our mission, and the future of
            AI-powered eSignatures.
          </p>
        </div>

        {/* Main Banner */}
        <div className="mb-12">
          <Image
            src={require(
              `assets/announcements/bolosign-nvidia-inception-program.webp`,
            )}
            alt="BoloSign Joins NVIDIA Inception Program"
            width={1200}
            height={600}
            className="w-full h-auto rounded-lg border"
          />
        </div>

        {/* Article Content */}
        <div className="prose prose-lg max-w-none">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            The Mission That Guided Us
          </h2>
          <p className="text-gray-700 mb-6">
            From day one, BoloSign wasn't built to be another fancy product… it
            was built to solve a problem.
          </p>
          <p className="text-gray-700 mb-6">
            We saw an opportunity to redefine how organizations whether
            startups, agencies, legal teams, or enterprises get documents
            signed. We focused on clarity, fairness (flat pricing, no hidden
            fees), reliability, and thoughtful design.
          </p>
          <p className="text-gray-700 mb-8">
            That foundational purpose has brought thousands of diverse teams to
            our platform. And now, our next chapter begins.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            We're officially part of the{" "}
            <strong>NVIDIA Inception Program</strong>
          </h2>
          <p className="text-gray-700 mb-6">
            We're proud to share that{" "}
            <strong>
              BoloSign has been accepted into the NVIDIA Inception Program
            </strong>
            , a global initiative that backs forward-thinking companies defining
            the future of AI innovation.
          </p>
          <p className="text-gray-700 mb-8">
            This isn't a check-the-box program. It's access to deep technical
            resources, strategic mentorship, and early-stage AI frameworks. It
            spells credibility. It means NVIDIA not just as a name, but as a
            partner is in our corner.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            The Power of Partnership
          </h2>
          <p className="text-gray-700 mb-6">
            The NVIDIA Inception Program gives BoloSign exclusive access to
            industry-leading technology and expertise. We're talking about the
            latest advancements in GPU computing, scalable AI infrastructure,
            and collaborative opportunities with visionary companies shaping
            tomorrow.
          </p>
          <p className="text-gray-700 mb-4">
            With NVIDIA's backing, BoloSign is positioned to:
          </p>
          <ul className="list-disc pl-6 mb-8 text-gray-700 space-y-2">
            <li>
              Leverage world-class AI acceleration for digital agreements,
              security checks, and data analytics.
            </li>
            <li>
              Innovate rapidly, adopting the latest tools and frameworks for
              unmatched reliability and speed.
            </li>
            <li>
              Collaborate with leading startups and industry experts, unlocking
              insights, best practices, and new go-to-market strategies.
            </li>
          </ul>
          <div className="mb-8">
            <Image
              src={require(
                `assets/announcements/bolosign-nvidia-inception-program-header2.webp`,
              )}
              alt="BoloSign Joins NVIDIA Inception Program"
              width={1200}
              height={600}
              className="w-full h-auto rounded-lg border"
            />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            What BoloSign Customers Can Expect
          </h2>
          <p className="text-gray-700 mb-4">
            Our collaboration with NVIDIA isn't just a badge it's a promise to
            deliver more:
          </p>
          <ul className="list-disc pl-6 mb-8 text-gray-700 space-y-3">
            <li>
              <strong>Smarter, Faster Agreements:</strong> Harnessing advanced
              AI and cloud resources, BoloSign will deliver even faster, smarter
              eSignature workflows, improving efficiency at scale.
            </li>
            <li>
              <strong>Enterprise-Grade Security:</strong> Enhanced compliance
              features powered by NVIDIA testing and support.
            </li>
            <li>
              <strong>Cutting-Edge Innovation:</strong> Early access to new
              technologies and methodologies, so our users reap the rewards
              before the competition.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            A Journey That Benefits All
          </h2>
          <p className="text-gray-700 mb-6">
            From small startups closing their first major deal to global
            enterprises automating thousands of contracts, BoloSign's journey
            with NVIDIA Inception is about championing every business trying to
            move faster, smarter, and more securely. Our story is driven by our
            users—their growth, challenges, and the future they build.
          </p>
          <p className="text-gray-700 mb-8">
            Our engineers, product managers, and customer success teams are
            already collaborating with NVIDIA experts to integrate next-gen
            features and create more value for BoloSign users. This landmark
            partnership is not the finish line, it's the launchpad for a new era
            of digital agreements.
          </p>

          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Invitation to Join Us
          </h3>
          <p className="text-gray-700 mb-8">
            If you're already a user, thanks for believing in us early. If
            you're just browsing know this… BoloSign now carries vote of
            confidence from one of the world's most respected names in AI, and
            we're just getting started.
          </p>

          <div
            style={{
              background: "linear-gradient(275deg, #c86aff -8.37%, #3164ff)",
            }}
            className="rounded-2xl p-8 mb-8 text-center"
          >
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to see what smarter signing feels like?
            </h3>
            <p className="text-gray-50 mb-6 max-w-2xl mx-auto">
              Experience the future of document signing with BoloSign, now
              enhanced by NVIDIA's cutting-edge AI technology.
            </p>
            <div className="text-center flex items-center gap-4 flex-wrap justify-center">
              {/* <CLMButtonSecondary
                buttonText="Start Your Free Trial"
                buttonUrl={`https://signature.boloforms.com/register?from=NVIDIA_ANNOUNCEMENT${createUTMQueryParams(
                  {
                    from: "NVIDIA_ANNOUNCEMENT",
                  }
                )}`}
                className=""
              /> */}
              <Link
                href="/clm/request-demo"
                className="inline-block px-6 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-purple-50 hover:text-blue-600 transition-colors"
              >
                Request a Demo
              </Link>
            </div>
          </div>

          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Closing Thought
          </h3>
          <p className="text-gray-700 mb-4">
            Some companies chase funding. Some chase features. We chose trust.
          </p>
          <p className="text-gray-700 mb-4">
            Joining NVIDIA Inception doesn't change who we are it accelerates
            everything we can become.
          </p>
          <p className="text-gray-700 mb-6">Let's sign smarter.</p>
          <p className="text-gray-700 font-semibold">— Team BoloSign</p>
        </div>

        {/* Footer CTA */}
      </div>
      <div className="w-full pt-10 sm:pb-8 sm:pt-16 bg-dark mt-12 lg:mt-20">
        <div className="max-w-screen-xl mx-auto">
          <h1 className="text-3xl md:text-4xl leading-snug text-center mb-3 text-white font-semibold">
            5,000+ Companies <br /> Use BoloForms Around the World
          </h1>
          <AnimatedLogos />
        </div>
      </div>
    </>
  );
};

export default NvidiaInceptionProgram;
